import React, { useState, useEffect, useCallback, useMemo } from 'react';
import ScSpinner from '../ScSpinner';
import { fetchExternalApis, createExternalApi, updateExternalApi } from '../../studyConfigurationApi';

const API_CODE_OPTIONS = [
  { value: 'SiteOnboard_SB', label: 'SiteOnboard_SB' },
  { value: 'SiteOnboard_Gateway', label: 'SiteOnboard_Gateway' },
  { value: 'Investigator_Gateway', label: 'Investigator_Gateway' },
];
const METHOD_OPTIONS = [
  { value: 'GET', label: 'GET' },
  { value: 'POST', label: 'POST' },
];

const emptyDraft = (availableCode) => ({
  apiCode: availableCode || '',
  url: '',
  methodType: 'POST',
  isActive: true,
});

const ExternalApisTab = () => {
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [success, setSuccess] = useState(null);
  const [saving, setSaving] = useState(false);
  const [editingKey, setEditingKey] = useState(null);
  const [draft, setDraft] = useState(null);
  const [adding, setAdding] = useState(false);

  const usedCodes = useMemo(
    () => new Set(rows.map((r) => (r.apiCode || '').trim()).filter(Boolean)),
    [rows]
  );
  const availableCodes = useMemo(
    () => API_CODE_OPTIONS.filter((o) => !usedCodes.has(o.value)),
    [usedCodes]
  );
  const canAdd = availableCodes.length > 0;

  const load = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const res = await fetchExternalApis();
      setRows(res.data || []);
    } catch (e) {
      setError(e.response?.data?.error || 'Failed to load external APIs.');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    load();
  }, [load]);

  const startAdd = () => {
    if (!canAdd) return;
    setAdding(true);
    setEditingKey(null);
    setDraft(emptyDraft(availableCodes[0].value));
    setSuccess(null);
    setError(null);
  };

  const startEdit = (row) => {
    setAdding(false);
    setEditingKey(row.externalApiNo);
    setDraft({
      apiCode: row.apiCode || '',
      url: row.url || '',
      methodType: row.methodType || 'POST',
      isActive: row.isActive !== false,
    });
    setSuccess(null);
    setError(null);
  };

  const cancel = () => {
    setAdding(false);
    setEditingKey(null);
    setDraft(null);
  };

  const save = async () => {
    if (!draft) return;
    setSaving(true);
    setError(null);
    setSuccess(null);
    try {
      const body = {
        apiCode: draft.apiCode,
        url: draft.url,
        methodType: draft.methodType,
        isActive: draft.isActive,
      };
      if (adding) {
        await createExternalApi(body);
        setSuccess('External API created.');
      } else {
        await updateExternalApi(editingKey, body);
        setSuccess('External API updated.');
      }
      cancel();
      await load();
    } catch (e) {
      setError(e.response?.data?.error || 'Save failed.');
    } finally {
      setSaving(false);
    }
  };

  if (loading) return <ScSpinner />;

  return (
    <div className="sc-external-apis">
      {error && <div className="sc-alert sc-alert-error">{error}</div>}
      {success && <div className="sc-alert sc-alert-success">{success}</div>}
      <p className="sc-empty" style={{ fontStyle: 'normal', marginBottom: '0.75rem' }}>
        SiteOnboard_SB runs after site create and site onboarding. SiteOnboard_Gateway runs during site onboarding. Investigator_Gateway runs when an investigator is added. Inactive rows are skipped.
      </p>
      <div className="sc-toolbar">
        <button
          type="button"
          className="sc-btn sc-btn-primary"
          onClick={startAdd}
          disabled={!canAdd || adding || editingKey != null}
          title={!canAdd ? 'All API codes are already configured.' : undefined}
        >
          Add external API
        </button>
      </div>

      {(adding || editingKey != null) && draft && (
        <div className="sc-email-gateway-form" style={{ marginBottom: '1rem' }}>
          <div className="sc-form-row">
            <label>API code</label>
            <select
              value={draft.apiCode}
              disabled={!adding}
              onChange={(e) => setDraft({ ...draft, apiCode: e.target.value })}
            >
              {(adding ? availableCodes : API_CODE_OPTIONS).map((o) => (
                <option key={o.value} value={o.value}>{o.label}</option>
              ))}
            </select>
          </div>
          <div className="sc-form-row">
            <label>URL</label>
            <input
              value={draft.url}
              onChange={(e) => setDraft({ ...draft, url: e.target.value })}
              maxLength={1000}
            />
          </div>
          <div className="sc-form-row">
            <label>Method</label>
            <select
              value={draft.methodType}
              onChange={(e) => setDraft({ ...draft, methodType: e.target.value })}
            >
              {METHOD_OPTIONS.map((o) => (
                <option key={o.value} value={o.value}>{o.label}</option>
              ))}
            </select>
          </div>
          <div className="sc-form-row">
            <label>
              <input
                type="checkbox"
                checked={draft.isActive}
                onChange={(e) => setDraft({ ...draft, isActive: e.target.checked })}
              />
              {' '}Active
            </label>
          </div>
          <div className="sc-toolbar">
            <button type="button" className="sc-btn sc-btn-primary" onClick={save} disabled={saving}>
              {saving ? 'Saving…' : 'Save'}
            </button>
            <button type="button" className="sc-btn" onClick={cancel} disabled={saving}>Cancel</button>
          </div>
        </div>
      )}

      {rows.length === 0 ? (
        <p className="sc-empty">No external APIs. Add a code or run the ExternalApiMst SQL scripts.</p>
      ) : (
        <div className="sc-grid-wrap">
          <table className="sc-grid">
            <thead>
              <tr>
                <th>API code</th>
                <th>URL</th>
                <th>Method</th>
                <th>Active</th>
                <th>Actions</th>
              </tr>
            </thead>
            <tbody>
              {rows.map((r) => (
                <tr key={r.externalApiNo} className={editingKey === r.externalApiNo ? 'sc-row-editing' : ''}>
                  <td>{r.apiCode}</td>
                  <td>{r.url}</td>
                  <td>{r.methodType}</td>
                  <td>{r.isActive ? 'Yes' : 'No'}</td>
                  <td>
                    <button
                      type="button"
                      className="sc-btn sc-btn-link"
                      onClick={() => startEdit(r)}
                      disabled={adding || editingKey != null}
                    >
                      Edit
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
};

export default ExternalApisTab;
